import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';

export default function BookingChoice() {
  const navigate = useNavigate();
  const { t } = useTranslation('crisis');

  const handleChoice = (choice: 'crisis' | 'regular') => {
    sessionStorage.setItem('booking_choice_made', 'true');
    
    if (choice === 'crisis') {
      navigate('/crisis-resources');
    } else {
      navigate('/book-an-appointment');
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-grow pt-16">
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 min-h-full">
          <div className="container mx-auto px-4 py-12 md:py-20">
            <div className="max-w-5xl mx-auto">
              <h1 className="text-3xl md:text-5xl font-bold text-center text-earth-800 mb-4">
                {t('choiceTitle')}
              </h1>
              <p className="text-center text-earth-600 mb-12 text-lg">
                {t('choiceSubtitle', 'Please select the type of support you need')}
              </p>

              <div className="grid md:grid-cols-2 gap-6 md:gap-8">
                {/* Crisis Button */}
                <button
                  onClick={() => handleChoice('crisis')}
                  className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-red-500 to-red-600 p-8 md:p-12 text-white shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 min-h-[300px] flex flex-col items-center justify-center"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-red-400/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="relative z-10 flex flex-col items-center text-center space-y-6">
                    <div className="bg-white/20 backdrop-blur-sm rounded-full p-6">
                      <AlertTriangle className="h-16 w-16" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-bold mb-3">
                        {t('crisisButton')}
                      </h2>
                      <p className="text-red-50 text-sm md:text-base">
                        {t('crisisDescription', 'Access immediate crisis support and emergency resources')}
                      </p>
                    </div>
                  </div>
                </button>

                {/* Regular Booking Button */}
                <button
                  onClick={() => handleChoice('regular')}
                  className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 p-8 md:p-12 text-white shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 min-h-[300px] flex flex-col items-center justify-center"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-400/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="relative z-10 flex flex-col items-center text-center space-y-6">
                    <div className="bg-white/20 backdrop-blur-sm rounded-full p-6">
                      <Calendar className="h-16 w-16" />
                    </div>
                    <div>
                      <h2 className="text-2xl md:text-3xl font-bold mb-3">
                        {t('regularButton')}
                      </h2>
                      <p className="text-blue-50 text-sm md:text-base">
                        {t('regularDescription', 'Schedule a session with a practitioner')}
                      </p>
                    </div>
                  </div>
                </button>
              </div>

              {/* Professional Note */}
              <div className="mt-8 bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-700 font-medium text-sm md:text-base text-center">
                  {t('professionalNote')}
                </p>
              </div>

              <div className="mt-8 text-center text-sm text-earth-600">
                <p>{t('privacyNote', 'Your privacy and safety are our top priority')}</p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
